import { Component, Input, Output, EventEmitter, OnDestroy, OnInit, signal, computed, HostListener, ElementRef, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CdkDrag, CdkDropList, CdkDragDrop, moveItemInArray, transferArrayItem, copyArrayItem, CdkDragPlaceholder, CdkDragPreview, CdkDragHandle } from '@angular/cdk/drag-drop';
import { TreeItem, TreeType, TreeLevel, HRoute } from '../tree.model';
import { HighlightPipe } from '../shared/highlight.pipe';
import { AutofocusDirective } from '../shared/autofocus.directive';
import { DragDropService } from '../drag-drop.service';
import { TreeInteractionService } from '../tree-interaction.service';
import { DiffPipe } from '../shared/diff.pipe';
import { TreeDataService } from '../tree-data.service';
import { UndoRedoService } from '../undo-redo.service';
import masterData from '../content-editor/builder.json';
import { ExpandPageHeightInfo } from '../content-editor/expand-page-height.service';

type MasterContentTemplate = {
    config?: {
        sections?: HRoute[];
    };
};

const MASTER_ROUTE_TEMPLATES = ((masterData as unknown) as MasterContentTemplate).config?.sections ?? [];


@Component({
    selector: 'app-tree',
    standalone: true,
    imports: [CommonModule, FormsModule, CdkDrag, CdkDropList, CdkDragPlaceholder, CdkDragPreview, CdkDragHandle, HighlightPipe, DiffPipe, AutofocusDirective],
    templateUrl: './tree.component.html',
    styleUrl: './tree.component.scss'
})
export class TreeComponent implements OnInit, OnDestroy {
    @Input() items: TreeItem[] = [];
    @Input() level: number = 0;
    @Input() treeType: TreeType = TreeType.Source;
    @Input() isEditingEnabled: boolean = true;
    @Input() searchTerm: string = '';
    @Input() isDualPane: boolean = false;
    @Input() isReorderEnabled: boolean | null = null;
    @Input() routeTransferMode: boolean = false;
    @Input() sidebarReorderMode: boolean = false;
    @Input() selectedItemId: string | null = null;
    @Input() showSelectButton: boolean = false;
    // Rendered /expand heights for page sub-components, keyed by page item id.
    @Input() pageHeights: Map<string, ExpandPageHeightInfo> | null = null;

    get canReorder(): boolean {
        return this.isReorderEnabled !== null ? this.isReorderEnabled : this.isEditingEnabled;
    }

    @Output() orderChanged = new EventEmitter<void>();
    @Output() itemClicked = new EventEmitter<TreeItem>();
    @Output() nameChanged = new EventEmitter<void>();


    listId = '';
    connectedLists = signal<string[]>([]);
    isDraggingFromThisList = false;
    trackById = (_: number, x: any) => x.id;
    draggingId: string | null = null;
    activeMenuItemId: string | null = null;
    activeAdvancedItemId: string | null = null;
    activeTagPickerItemId: string | null = null;
    activeTextEditItemId: string | null = null;
    inlineEditorProperties: { key: string, value: any }[] = [];
    private textEditSnapshotCaptured = false;
    readonly editorElementTypes = [
        'action-button-example',
        'answer-item',
        'br',
        'dropdown',
        'download-button',
        'email',
        'email-p',
        'header',
        'helper',
        'hr',
        'likert',
        'listitem',
        'p',
        'question',
        'rating-component',
        'reflection-image',
        'textarea',
        'watch-now-button',
        'yes-no',
    ];

    // Computed property to check if this list should be highlighted
    isTarget = computed(() => {
        const draggingLevel = this.dragDropService.draggedLevel();
        const draggingType = this.dragDropService.draggedType();

        // Basic level match required or compatible levels
        const isLevelCompatible = (listLvl: number, dragLvl: number | null) => {
            if (dragLvl === null) return false;
            if (dragLvl === listLvl) return true;
            if (listLvl === TreeLevel.Element && dragLvl === TreeLevel.SubComponent) return true;
            if (listLvl === TreeLevel.SubComponent && dragLvl === TreeLevel.SubComponentElement) return true;
            return false;
        };

        if (!isLevelCompatible(this.level, draggingLevel)) return false;


        // Connection rules:
        // If dragging Source: can drop in Source OR Target
        if (draggingType === TreeType.Source) {
            // If Dual Pane is active, Source cannot be a target (only Target tree is target)
            if (this.isDualPane && this.treeType === TreeType.Source) return false;

            return true;
        }

        // If dragging Target: can drop in Target ONLY (and this itself is Target)
        if (draggingType === TreeType.Target) {
            return this.treeType === TreeType.Target;
        }

        return false;
    });


    isHoveringTarget = computed(() => this.dragDropService.hoveredType() === TreeType.Target);

    // Route Addition
    showAddRouteDropdown = false;
    readonly masterRouteOptions = this.buildDistinctMasterRoutes();

    get canAddMasterRoute(): boolean {
        if (!this.sidebarReorderMode || this.routeTransferMode) return false;
        if (this.isDualPane && this.treeType === TreeType.Source) return false;
        return this.treeType === TreeType.Source && this.level === 0;
    }

    get routeDropdownOptions(): Array<{ value: string, label: string }> {
        return this.masterRouteOptions.map(option => ({
            value: option.route,
            label: option.label
        }));
    }

    get shouldShowRouteAddButton(): boolean {
        return this.canAddMasterRoute;
    }


    toggleAddRouteDropdown(event?: Event) {
        if (event) event.stopPropagation();
        this.showAddRouteDropdown = !this.showAddRouteDropdown;
    }

    toggleItemMenu(item: TreeItem, event: Event) {
        event.stopPropagation();
        this.activeMenuItemId = this.activeMenuItemId === item.id ? null : item.id;
    }

    closeItemMenu() {
        this.activeMenuItemId = null;
    }

    closeAdvancedSettings() {
        this.activeAdvancedItemId = null;
    }

    openTagPicker(item: TreeItem, event: Event) {
        if (!this.isEditable(item) || !this.isEditingEnabled || item.isDeleted) return;
        event.stopPropagation();
        this.activeTagPickerItemId = this.activeTagPickerItemId === item.id ? null : item.id;
        this.activeAdvancedItemId = null;
    }

    selectTagType(item: TreeItem, newType: string, event: Event) {
        event.stopPropagation();
        this.undoRedoService.notifyBeforeMutation();

        if (item.item) {
            (item.item as any).type = newType;
            if (newType === 'br' || newType === 'hr') {
                item.name = '';
                item.updatedText = undefined;
            }
        }

        if (item.originalTag === undefined) {
            item.originalTag = item.tag;
        }
        item.tag = newType;
        if (item.tag === item.originalTag) {
            item.originalTag = undefined;
        }

        item.isUpdated = item.isNew || item.updatedText !== undefined || !!item.propertiesUpdated || item.originalTag !== undefined;
        this.activeTagPickerItemId = null;

        this.bubbleNameToParent(item, item.updatedText ?? item.name ?? '');
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        if (this.showAddRouteDropdown && this.elRef && !this.elRef.nativeElement.contains(event.target)) {
            this.showAddRouteDropdown = false;
        }
        if (this.activeMenuItemId) {
            this.activeMenuItemId = null;
        }
        if (this.activeAdvancedItemId && this.elRef && !this.elRef.nativeElement.contains(event.target)) {
            this.activeAdvancedItemId = null;
        }
        if (this.activeTagPickerItemId && this.elRef && !this.elRef.nativeElement.contains(event.target)) {
            this.activeTagPickerItemId = null;
        }
    }

    addMasterRoute(routePath: string) {
        if (!this.canAddMasterRoute) return;

        const template = this.masterRouteOptions.find(option => option.route === routePath)?.template;
        if (!template) {
            return;
        }

        this.showAddRouteDropdown = false;
        this.undoRedoService.notifyBeforeMutation();

        const newRoute = this.buildRouteFromMasterTemplate(template);

        this.markTreeAsNew(newRoute);
        newRoute.expanded = true;

        this.items.push(newRoute);
        this.orderChanged.emit();
    }

    selectRouteOption(routePath: string) {
        this.addMasterRoute(routePath);
    }

    ngOnInit() {
        // encode treeType in ID for robust detection
        this.listId = `list-${this.treeType}-${this.level}-${Math.random().toString(36).substr(2, 9)}`;
        this.dragDropService.register(this.listId, this.level, this.treeType);

        // Subscribe to connections for this level
    }

    get connectedListIds() {
        return this.dragDropService.getConnectedLists(this.level, this.treeType)();
    }

    ngOnDestroy() {
        this.dragDropService.unregister(this.listId, this.level, this.treeType);
    }

    drop(event: CdkDragDrop<TreeItem[]>) {
        this.undoRedoService.notifyBeforeMutation();
        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
            this.orderChanged.emit();
        } else {
            // Check if we are dragging Source -> Target
            // We parse the ID of the previous container to check if it was 'source'
            // ID format: list-TYPE-LEVEL-RANDOM
            const previousId = event.previousContainer.id;
            const isSourceCheck = previousId.includes('list-source-');

            const isCopy = this.treeType === TreeType.Target && isSourceCheck;

            console.log('Drop debug:', {
                fromId: previousId,
                toId: this.listId,
                isSource: isSourceCheck,
                isTargetTree: this.treeType === TreeType.Target,
                isCopy: isCopy
            });

            if (isCopy) {
                copyArrayItem(
                    event.previousContainer.data,
                    event.container.data,
                    event.previousIndex,
                    event.currentIndex,
                );

                // The copied item shares the source's reference/IDs; replace it with a
                // deep clone that has fresh IDs and is re-leveled to this list's level.
                const copiedItem = event.container.data[event.currentIndex];
                event.container.data[event.currentIndex] = this.treeDataService.cloneTemplate(copiedItem, this.level);

            } else {
                // Normal move (Source->Source, Target->Target)
                transferArrayItem(
                    event.previousContainer.data,
                    event.container.data,
                    event.previousIndex,
                    event.currentIndex,
                );

                // Update the moved item's level to match the new container's level, unless it's a compatible mixed type
                const movedItem = event.container.data[event.currentIndex];

                const isMixed = (this.level === TreeLevel.Element && movedItem.level === TreeLevel.SubComponent) ||
                    (this.level === TreeLevel.SubComponent && movedItem.level === TreeLevel.SubComponentElement);

                if (!isMixed) {
                    movedItem.level = this.level;
                }
            }
            this.orderChanged.emit();
        }
    }

    expandAll() {
        this.expandCollapseRecursive(this.items, true);
    }

    collapseAll() {
        this.expandCollapseRecursive(this.items, false);
    }

    private expandCollapseRecursive(items: TreeItem[], isExpanded: boolean) {
        if (!items) return;

        items.forEach(item => {
            if (item.children && item.children.length > 0) {
                item.expanded = isExpanded;
                this.expandCollapseRecursive(item.children, isExpanded);
            }
        });
    }

    onDragStarted(item: TreeItem) {
        const parentTag = this.parentItem?.tag;
        this.dragDropService.startDrag(item.level, this.treeType, parentTag);
        item.expanded = false;
        this.isDraggingFromThisList = true;
        if (this.treeType === TreeType.Source) {
            this.draggingId = item.id;
        }
    }

    onDragEnded(item: TreeItem) {

        this.isDraggingFromThisList = false;
        this.dragDropService.endDrag();
        this.draggingId = null;
    }

    /**
     * Predicate function that only allows dropping if the dragged item's level matches the current list's level.
    /**
     * Predicate function that only allows dropping if the dragged item's level matches the current list's level.
     * NOTE: With connected lists by level, this is less strictly necessary for "connection" but still good for "drop allowed" validation.
     * Predicate function that only allows dropping if the dragged item's level matches the current list's level,
     * or if the levels are compatible (e.g. SubComponent/Element in the same container).
     */
    enterPredicate = (drag: CdkDrag<TreeItem>, drop: CdkDropList) => {
        // If Dual Pane is on, prevent drops on Source tree
        if (this.isDualPane && this.treeType === TreeType.Source) return false;

        const dragLevel = drag.data.level;
        const listLevel = this.level;

        // Exact match
        if (dragLevel === listLevel) {
            // For Component level: only allow if parent route tags match
            if (listLevel === TreeLevel.Component) {
                const dragParentTag = this.dragDropService.draggedParentTag();
                const dropParentTag = this.parentItem?.tag;
                if (dragParentTag && dropParentTag && dragParentTag !== dropParentTag) {
                    return false;
                }
            }
            return true;
        }

        // Compatibility rules:
        // List Level 2 (Element Container) accepts Level 3 (SubComponent)
        if (listLevel === TreeLevel.Element && dragLevel === TreeLevel.SubComponent) return true;

        // List Level 3 (SubComponent Container - actually via recursion 2+1=3?) accepts Level 4 (SubComponentElement)
        // Wait, SubComponent children container is rendered with level + 1.
        // If SubComponent is Level 3. Children container is Level 4?
        // Let's check recursion.
        // If SubComponent is in list of Level 2.
        // It is rendered. Its children are [level]="level + 1" = 3.
        // So children container is Level 3.
        // Items inside are SubComponentElement (Level 4).
        // So List Level 3 accepts Item Level 4.
        if (listLevel === TreeLevel.SubComponent && dragLevel === TreeLevel.SubComponentElement) return true;

        return false;
    };

    onListEntered() {
        // Improve robustness: only update hover if we are actually dragging
        if (this.dragDropService.draggedType()) {
            this.dragDropService.setHoveredType(this.treeType);
        }
    }

    toggle(item: TreeItem) {
        if (this.routeTransferMode) {
            return;
        }
        if (this.hasVisibleChildren(item) || this.canAddChildrenTo(item)) {
            item.expanded = !item.expanded;
        } else if (this.sidebarReorderMode) {
            this.itemClicked.emit(item);
        }
    }

    getLevelName(level: number): string {
        return TreeLevel[level];
    }

    // Expose enum to template
    TreeLevel = TreeLevel;

    isEditable(item: TreeItem): boolean {
        if (this.routeTransferMode || this.sidebarReorderMode) {
            return false;
        }
        return item.level === TreeLevel.Element || item.level === TreeLevel.SubComponentElement || item.level === TreeLevel.SubComponent;
    }

    /**
     * Inline text/value editing applies only to leaf elements that actually
     * carry a text value. Containers such as a page (SubComponent) have no
     * inline text, so editing them is a no-op that isn't persisted — clicking
     * one should fall through to expand/collapse, not open a text editor.
     * Properties editing for these containers stays available via isEditable.
     */
    isTextEditable(item: TreeItem): boolean {
        if (this.routeTransferMode || this.sidebarReorderMode) {
            return false;
        }
        return item.level === TreeLevel.Element || item.level === TreeLevel.SubComponentElement;
    }

    requestEdit(item: TreeItem, event: Event) {
        if (!this.isTextEditable(item) || !this.isEditingEnabled) return;
        event.stopPropagation();
        this.interactionService.startEdit(item);
    }

    beginTextEdit(item: TreeItem, event: Event) {
        if (!this.isTextEditable(item) || !this.isEditingEnabled || item.isDeleted) {
            return;
        }
        event.stopPropagation();
        if (this.interactionService.editingItem() !== item) {
            this.interactionService.startEdit(item);
        }
        this.activeTextEditItemId = item.id;
        this.activeAdvancedItemId = null;
    }

    isInlineEditing(item: TreeItem): boolean {
        return this.interactionService.editingItem() === item && this.activeTextEditItemId === item.id;
    }

    isAdvancedSettingsOpen(item: TreeItem): boolean {
        return this.activeAdvancedItemId === item.id;
    }

    shouldShowInlineValueInput(item: TreeItem): boolean {
        const type = (item.item as any)?.type;
        return type !== 'hr' && type !== 'br';
    }

    getInlineItemType(item: TreeItem): string {
        return (item.item as any)?.type || item.tag || 'p';
    }

    openAdvancedSettings(item: TreeItem, event: Event) {
        if (!this.isEditable(item) || !this.isEditingEnabled || item.isDeleted) {
            return;
        }
        event.stopPropagation();
        if (this.interactionService.editingItem() !== item) {
            this.interactionService.startEdit(item);
        }
        this.activeTextEditItemId = null;
        this.activeAdvancedItemId = item.id;
        this.activeMenuItemId = null;
    }

    updateInlineType(newType: string) {
        const item = this.interactionService.editingItem();
        if (!item) {
            return;
        }

        this.captureInlineSnapshot();

        if (item.item) {
            (item.item as any).type = newType;
            if (newType === 'br' || newType === 'hr') {
                item.name = '';
                item.updatedText = undefined;
            }
        }

        if (item.originalTag === undefined) {
            item.originalTag = item.tag;
        }
        item.tag = newType;
        if (item.tag === item.originalTag) {
            item.originalTag = undefined;
        }

        item.isUpdated = item.isNew || item.updatedText !== undefined || !!item.propertiesUpdated || item.originalTag !== undefined;
    }

    updateInlineText(text: string) {
        const item = this.interactionService.editingItem();
        if (!item) {
            return;
        }

        this.captureInlineSnapshot();

        const current = text.trim() || '';
        if (item.isNew) {
            item.name = current;
            item.updatedText = undefined;
        } else {
            const original = item.name.trim() || '';
            item.updatedText = current !== original ? current : undefined;
        }

        item.isUpdated = item.isNew || item.updatedText !== undefined || !!item.propertiesUpdated;

        this.bubbleNameToParent(item, current);
    }

    private bubbleNameToParent(item: TreeItem, text: string) {
        if (!this.parentItem || item.level !== TreeLevel.Element) return;
        const firstElement = this.items.find(i => i.level === TreeLevel.Element);
        if (firstElement !== item) return;
        this.parentItem.name = text;
        this.nameChanged.emit();
    }

    onChildNameChanged() {
        if (this.parentItem) {
            const firstComponent = this.items.find(i => i.level === TreeLevel.Component);
            if (firstComponent) {
                this.parentItem.name = firstComponent.name;
            }
        }
        this.nameChanged.emit();
    }

    addInlineProperty(event?: Event) {
        if (event) {
            event.stopPropagation();
        }
        this.captureInlineSnapshot();
        this.inlineEditorProperties.push({ key: '', value: '' });
    }

    removeInlineProperty(index: number, event?: Event) {
        if (event) {
            event.stopPropagation();
        }
        this.captureInlineSnapshot();
        this.inlineEditorProperties.splice(index, 1);
    }

    saveInlineEdit(event?: Event) {
        if (event) {
            event.stopPropagation();
        }

        const item = this.interactionService.editingItem();
        if (item) {
            const newProps: Record<string, any> = {};
            this.inlineEditorProperties.forEach(prop => {
                if (prop.key && prop.key.trim() !== '') {
                    newProps[prop.key] = prop.value;
                }
            });

            item.updatedProperties = newProps;

            let isDiff = false;
            if (item.item) {
                const originalProps = (item.item as any).properties || {};
                isDiff = JSON.stringify(newProps) !== JSON.stringify(originalProps);
                item.propertiesUpdated = isDiff;
            }

            item.isUpdated = item.isNew || !!item.updatedText || !!item.propertiesUpdated;

            if (!isDiff) {
                item.updatedProperties = undefined;
            }
        }

        this.interactionService.saveEdit();
        this.resetInlineEditorState();
    }

    cancelInlineEdit(event?: Event) {
        if (event) {
            event.stopPropagation();
        }
        this.interactionService.cancelEdit();
        this.resetInlineEditorState();
    }

    markInlineMutation() {
        this.captureInlineSnapshot();
    }

    shouldShowEditIcon(item: TreeItem): boolean {
        if (this.isDualPane) return false;
        if (!this.isEditable(item)) return false;
        return true;
    }

    canDelete(item: TreeItem): boolean {
        if (this.sidebarReorderMode) {
            return item.level === TreeLevel.Route || item.level === TreeLevel.Component;
        }
        if (this.isDualPane && this.treeType === TreeType.Source) return false;
        return true;
    }

    hasProperties(item: TreeItem): boolean {
        return !!((item.item as any)?.properties || item.updatedProperties);
    }

    shouldShowPropertiesIcon(item: TreeItem): boolean {
        if (this.routeTransferMode || this.sidebarReorderMode) return false;
        if (this.isDualPane) return false;
        return this.hasProperties(item) || this.isEditingEnabled;
    }

    canDuplicateItem(item: TreeItem): boolean {
        if (!this.sidebarReorderMode) {
            return false;
        }

        return item.level === TreeLevel.Route || item.level === TreeLevel.Component;
    }

    duplicateItem(item: TreeItem, event: Event) {
        event.stopPropagation();

        if (!this.canDuplicateItem(item)) {
            return;
        }

        const index = this.items.indexOf(item);
        if (index === -1) {
            return;
        }

        this.undoRedoService.notifyBeforeMutation();
        const clone = this.treeDataService.cloneTemplate(item, this.level);
        clone.expanded = item.expanded;

        if (item.isNew) {
            this.clearFromIdRecursive(clone);
        }

        this.items.splice(index + 1, 0, clone);
        this.orderChanged.emit();
    }

    getSidebarReorderItemLabel(item: TreeItem): string {
        if (item.level === TreeLevel.Route) {
            return item.name || this.getRouteItemTitle(item) || 'Untitled route';
        }
        return item.name || this.formatLabel((item.item as any)?.type ?? item.tag ?? '') || 'Untitled component';
    }

    getVisibleChildren(item: TreeItem): TreeItem[] {
        if (this.sidebarReorderMode) {
            if (item.level === TreeLevel.Route) {
                return item.children || [];
            }
            return [];
        }
        return item.children || [];
    }

    hasVisibleChildren(item: TreeItem): boolean {
        return this.getVisibleChildren(item).length > 0;
    }

    /**
     * True when `item` is a container the user can add children into (Route,
     * Component or SubComponent). Used so an empty container still renders its
     * children list and exposes the "+ Add" button — e.g. a newly-added page
     * with no elements yet. Gated by the same conditions that decide whether the
     * add button shows, so we never render an empty container div for nothing.
     */
    canAddChildrenTo(item: TreeItem): boolean {
        if (!this.isEditingEnabled || this.routeTransferMode || this.sidebarReorderMode) return false;
        if (this.isDualPane && this.treeType === TreeType.Source) return false;
        return item.level === TreeLevel.Route
            || item.level === TreeLevel.Component
            || item.level === TreeLevel.SubComponent;
    }

    getRouteItemTitle(item: TreeItem): string {
        const routePath = (item.item as any)?.route ?? item.tag ?? '';
        return this.formatLabel(routePath) || item.name || 'Untitled route';
    }

    getRouteItemPreview(item: TreeItem): string {
        return item.name || 'Drop into target to include this route';
    }

    private formatLabel(value: string): string {
        return (value || '')
            .replace(/^\//, '')
            .replace(/\//g, ' / ')
            .replace(/[-_]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .replace(/\b\w/g, char => char.toUpperCase());
    }

    private buildDistinctMasterRoutes(): Array<{ route: string, label: string, template: HRoute }> {
        const seen = new Set<string>();
        const options: Array<{ route: string, label: string, template: HRoute }> = [];

        for (const section of MASTER_ROUTE_TEMPLATES) {
            if (!section?.route || seen.has(section.route)) {
                continue;
            }

            seen.add(section.route);
            options.push({
                route: section.route,
                label: this.formatLabel(section.route) || section.route,
                template: section
            });
        }

        return options;
    }

    private buildRouteFromMasterTemplate(template: HRoute): TreeItem {
        const routeClone: HRoute = JSON.parse(JSON.stringify(template));
        routeClone.routeId = this.treeDataService.id();
        (routeClone as any).id = routeClone.routeId;
        routeClone.position = this.items.length;
        routeClone.properties = { ...(routeClone.properties || {}) };
        routeClone.components = (routeClone.components || []).map((component: any, componentIndex: number) => {
            const componentId = this.treeDataService.id();
            component.componentId = componentId;
            component.id = componentId;
            component.position = componentIndex;
            component.properties = { ...(component.properties || {}) };
            component.elements = (component.elements || []).map((element: any, elementIndex: number) => {
                const elementId = this.treeDataService.id();
                element.elementId = elementId;
                element.id = elementId;
                element.position = elementIndex;
                element.properties = { ...(element.properties || {}) };
                return element;
            });
            component.subComponents = (component.subComponents || []).map((subComponent: any, subComponentIndex: number) => {
                const subComponentId = this.treeDataService.id();
                subComponent.componentId = subComponentId;
                subComponent.id = subComponentId;
                subComponent.parentComponentId = componentId;
                subComponent.position = subComponentIndex;
                subComponent.properties = { ...(subComponent.properties || {}) };
                subComponent.elements = (subComponent.elements || []).map((element: any, elementIndex: number) => {
                    const elementId = this.treeDataService.id();
                    element.elementId = elementId;
                    element.id = elementId;
                    element.position = elementIndex;
                    element.properties = { ...(element.properties || {}) };
                    return element;
                });
                return subComponent;
            });
            return component;
        });

        const [routeItem] = this.treeDataService.buildRoutes([routeClone]);
        return routeItem;
    }

    private markTreeAsNew(item: TreeItem) {
        item.isNew = true;
        item.fromId = undefined;
        item.updatedProperties = (item.item as any)?.properties ? { ...((item.item as any).properties) } : {};
        item.children.forEach(child => this.markTreeAsNew(child));
    }

    private clearFromIdRecursive(item: TreeItem) {
        item.fromId = undefined;
        item.children.forEach(child => this.clearFromIdRecursive(child));
    }

    openProperties(item: TreeItem, event: Event) {
        event.stopPropagation();
        this.interactionService.openProperties(item, event as MouseEvent);
    }

    getOriginalValue(item: TreeItem): string {
        // If item is new, use current name as original to avoid diffing against "New Element" placeholder
        // If item is updated, the 'name' property has been patched with the original value from the override, so use that.
        if (item.isNew || item.isUpdated) {
            return item.name;
        }
        return (item.item as any)?.value || item.name;
    }

    remove(item: TreeItem, event: Event) {
        event.stopPropagation();
        if (!this.canDelete(item)) return;
        if (!this.isEditingEnabled && !this.sidebarReorderMode) return;

        this.undoRedoService.notifyBeforeMutation();

        if (item.isNew) {
            // Hard delete for initialized items that weren't saved
            const index = this.items.indexOf(item);
            if (index > -1) {
                this.items.splice(index, 1);
            }
            if (this.interactionService.editingItem() === item) {
                this.interactionService.editingItem.set(null); // Clear editing state
            }
            this.orderChanged.emit();
            return;
        }

        // Toggle deleted state (Soft Delete)
        item.isDeleted = !item.isDeleted;
        this.orderChanged.emit();
    }

    get isElementList(): boolean {
        // Allow for Element (2), SubComponent (3), SubComponentElement (4)
        return this.level >= TreeLevel.Element;
    }

    isElementItem(item: TreeItem): boolean {
        return item.level === TreeLevel.Element || item.level === TreeLevel.SubComponentElement;
        // Or check if NOT SubComponent folder
    }



    @Input() parentItem?: TreeItem;

    // ... (existing code matches)

    addLocalItem(event?: Event, requestedType?: string) {
        if (event) event.stopPropagation();
        this.undoRedoService.notifyBeforeMutation();

        // Calculate insertion index
        let insertIndex = 0;
        // Find last item index of correct type
        // For Components (Level 1): items are components
        // For Elements (Level 2+): items are elements

        insertIndex = this.items.length; // Default to end

        if (this.isElementList) {
            // Heuristic: insert after last valid element (before subcomponents if any)
            for (let i = this.items.length - 1; i >= 0; i--) {
                if (this.isElementItem(this.items[i])) {
                    insertIndex = i + 1;
                    break;
                }
            }
        }

        let newItem: TreeItem;

        if (this.level === TreeLevel.Component && this.parentItem) {
            // Verify we can add here
            if (!this.canAddComponent(this.parentItem)) return;

            const routeName = this.parentItem.tag;
            let newType = 'page'; // default

            if (['/video', '/videos', '/stories'].includes(routeName)) {
                newType = 'video';
            }
            if (['/reminders', '/actions'].includes(routeName)) {
                newType = 'action';
            }
            if (['/expand', '/expand-statement'].includes(routeName)) {
                newType = 'statement';
            }
            if (['/sometimes'].includes(routeName)) {
                newType = 'n-s-y';
            }

            // janp todo can add some smarts here to have predefined properties
            // eg for video, or action, or statement.

            newItem = {
                id: this.treeDataService.id(),
                name: '',
                position: insertIndex,
                level: TreeLevel.Component,
                children: [],
                expanded: true,
                isNew: true,
                tag: newType,
                item: {
                    componentId: this.treeDataService.id(),
                    type: newType,
                    elements: [],
                    properties: {}
                } as any
            };

        } else {
            // Element or SubComponentElement

            // Check if we specifically asked for a Page SubComponent (only valid in Statement)
            const isStatementParent = this.parentItem && (this.parentItem.item as any)?.type === 'statement';

            if (isStatementParent && requestedType === 'page') {

                insertIndex = this.items.length; // Always append subcomponents to the end

                newItem = {
                    id: this.treeDataService.id(),
                    name: 'New Page',
                    level: TreeLevel.SubComponent,
                    children: [],
                    position: insertIndex,
                    expanded: true,
                    isNew: true,
                    tag: 'page',
                    item: {
                        componentId: this.treeDataService.id(),
                        type: 'page',
                        elements: [],
                        properties: {}
                    } as any
                };

            } else {
                // Default Element creation
                newItem = {
                    id: this.treeDataService.id(),
                    name: 'New Element',
                    level: this.level === TreeLevel.SubComponent ? TreeLevel.SubComponentElement : this.level,
                    children: [],
                    position: insertIndex,
                    expanded: false,
                    isNew: true,
                    tag: 'p',
                    item: {
                        id: this.treeDataService.id(),
                        type: 'p',
                        value: 'New Element',
                        properties: {}
                    } as any
                };
            }
        }

        this.items.splice(insertIndex, 0, newItem);

        // Recalculate positions across the tree so the new item's updatedPosition
        // is set (generateOverrides reads position from updatedPosition). Without
        // this the item saves with no position until it is repositioned. Mirrors
        // the orderChanged emit that remove() fires.
        this.orderChanged.emit();

        // Only auto-start editing for Elements, not Components or SubComponents
        if (newItem.level !== TreeLevel.Component && newItem.level !== TreeLevel.SubComponent) {
            this.interactionService.startEdit(newItem);
        }
    }

    // --- Add Component/Element Helpers ---

    canAddComponent(item: TreeItem): boolean {
        if (!this.isEditingEnabled) return false;
        // Only allowed on Route level (0) items
        if (item.level !== TreeLevel.Route) return false;

        return true
    }

    isStatementParent(): boolean {
        return !!(this.parentItem && (this.parentItem.item as any)?.type === 'statement');
    }

    // No longer needed as public method for row button, but logic used internally
    // getAdderLabel(item: TreeItem)...

    getAddButtonLabel(): string {
        if (this.level === TreeLevel.Component && this.parentItem) {
            const routeName = (this.parentItem.item as any)?.route ?? this.parentItem.tag;

            if (['/reflection', '/feedback', '/welcome', '/intro', '/read'].includes(routeName)) return '+ Add Page';

            if (['/video', '/videos', '/stories'].includes(routeName)) return '+ Add Video';

            if (['/reminders', '/actions'].includes(routeName)) return '+ Add Action';

            if (['/expand', '/expand-statement', '/sometimes'].includes(routeName)) return '+ Add Statement';

            if (['/sometimes'].includes(routeName)) return '+ Add No, Sometimes, Yes';

            return '+ Add Component';
        }

        return '+ Add Element';
    }

    shouldShowAddHelperAtTop(): boolean {
        if (this.routeTransferMode || this.sidebarReorderMode) return false;
        if (!this.isEditingEnabled) return false;
        // Hide add buttons on Source Tree in Dual Pane mode
        if (this.isDualPane && this.treeType === TreeType.Source) return false;

        // Case 1: Elements List
        if (this.isElementList) {
            if (this.items.length === 0) return true;
            if (!this.isElementItem(this.items[0])) return true;
            return false;
        }

        // Case 2: Component List (Level 1)
        if (this.level === TreeLevel.Component && this.parentItem) {
            // Check if parent allows adding
            if (!this.canAddComponent(this.parentItem)) return false;
            // If empty, show at top
            if (this.items.length === 0) return true;
            // Otherwise, we rely on the "After" button unless we want one at top too?
            // Elements usually have one at top if empty.
            return false;
        }

        return false;
    }

    shouldShowAddHelperAfter(index: number): boolean {
        if (this.routeTransferMode || this.sidebarReorderMode) return false;
        if (!this.isEditingEnabled) return false;
        // Hide add buttons on Source Tree in Dual Pane mode
        if (this.isDualPane && this.treeType === TreeType.Source) return false;

        const current = this.items[index];
        const next = this.items[index + 1];

        // Case 1: Elements List
        if (this.isElementList) {
            if (!this.isElementItem(current)) return false;
            if (!next || !this.isElementItem(next)) return true;
            return false;
        }

        // Case 2: Component List (Level 1)
        if (this.level === TreeLevel.Component && this.parentItem) {
            if (!this.canAddComponent(this.parentItem)) return false;
            // Logic: Show after last component? Or after EVERY component?
            // Element logic: after last element (before subcomponents) OR if next is not element.
            // Components don't have sub-items in the same list usually.
            // Let's show after the LAST item.
            if (!next) return true;
        }

        return false;
    }

    constructor(
        public dragDropService: DragDropService,
        public interactionService: TreeInteractionService,
        private elRef: ElementRef,
        private treeDataService: TreeDataService,
        private undoRedoService: UndoRedoService
    ) {
        effect(() => {
            const item = this.interactionService.editingItem();
            if (!item) {
                this.resetInlineEditorState();
                return;
            }

            this.textEditSnapshotCaptured = false;
            const props = item.updatedProperties || (item.item as any)?.properties || {};
            this.inlineEditorProperties = Object.keys(props).map(key => ({
                key,
                value: props[key]
            }));
        });
    }

    private captureInlineSnapshot() {
        if (!this.textEditSnapshotCaptured) {
            this.undoRedoService.notifyBeforeMutation();
            this.textEditSnapshotCaptured = true;
        }
    }

    private resetInlineEditorState() {
        this.inlineEditorProperties = [];
        this.activeAdvancedItemId = null;
        this.activeTextEditItemId = null;
        this.textEditSnapshotCaptured = false;
    }
}
